import { ChevronLeft } from 'lucide-react'
import { useMemo } from 'react'

import LoadingSpinnerCentered from 'components/reusable/LoadingSpinnerCentered'
import { TextButton, TextLink } from 'slab'
import { formatMoneyValue, formatMonthName, getFirstName, getNextMonthName } from 'utils/billing'
import {
  useGetEmployersMembersIdMonthlyAllowance,
} from 'utils/web_api'

import { CostBreakdownItem } from './CostBreakdownItem'
import { useInvoice } from './InvoiceContext'

interface MemberInvoiceDetailsProps {
  memberInvoiceId: string
  onBack: () => void
}

export function MemberInvoiceDetails ({ memberInvoiceId, onBack }: MemberInvoiceDetailsProps) {
  const { invoice, enrichedMemberInvoices } = useInvoice()

  const memberInvoice = useMemo(() => {
    return enrichedMemberInvoices.find((mi) => mi.id === memberInvoiceId)
  }, [enrichedMemberInvoices, memberInvoiceId])

  const coverageDate = useMemo(() => {
    if (!invoice?.period_end_date) return null
    const endDate = new Date(invoice.period_end_date)
    return new Date(endDate.getFullYear(), endDate.getMonth(), 1).toISOString().split('T')[0]
  }, [invoice?.period_end_date])

  const planYear = useMemo(() => {
    return memberInvoice?.plan_year ?? (invoice?.period_end_date ? new Date(invoice.period_end_date).getFullYear() : null)
  }, [memberInvoice?.plan_year, invoice?.period_end_date])

  const {
    data: monthlyAllowanceData,
    isLoading: isLoadingAllowance,
  } = useGetEmployersMembersIdMonthlyAllowance(
    memberInvoice?.member_id || '',
    {
      coverage_date: coverageDate ?? '',
      plan_year: planYear ?? undefined,
    },
    {
      query: {
        enabled: !!memberInvoice && !!memberInvoice.member_id && !!coverageDate,
        retry: false,
      },
    }
  )

  if (!memberInvoice) {
    return (
      <div className='p-6'>
        <div className='text-gray-500'>Member invoice not found</div>
      </div>
    )
  }

  const monthName = formatMonthName(invoice.period_end_date)
  const periodStartFormatted = new Date(invoice.period_start_date).toLocaleString('en-US', { month: 'long', day: 'numeric' })
  const periodEndFormatted = new Date(invoice.period_end_date).toLocaleString('en-US', { month: 'long', day: 'numeric' })
  const periodEndDate = new Date(invoice.period_end_date)
  const nextMonthYearFormatted = `${getNextMonthName(invoice.period_end_date)} ${new Date(periodEndDate.getFullYear(), periodEndDate.getMonth() + 1, 1).getFullYear()}`

  const { amounts } = memberInvoice
  const prefundingAmount = amounts?.total_insurance_premiums
  const qmeTotal = amounts?.other
  const ineligibleSpend = memberInvoice.posttax_employee_qme_overage
  const qualifiedExpenses = memberInvoice.pretax_employer_qme_reimbursement
  const coverageCorrections = amounts?.coverage_corrections
  const totalAmount = amounts?.total

  const firstName = getFirstName(memberInvoice.full_name)
  const monthlyAllowance = monthlyAllowanceData?.monthly_allowance

  return (
    <div className='p-6'>
      <div className='mb-6'>
        <TextButton
          color='gray'
          leftIcon={ChevronLeft}
          onClick={onBack}
        >
          Back to {monthName} healthcare costs
        </TextButton>
      </div>

      <h2 className='heading-md mb-4'>{memberInvoice.full_name || '---'}</h2>

      <div className='flex items-center justify-between mb-6'>
        <span className='body-sm text-subdued'>Member cost breakdown</span>
        <div className='bg-white rounded-[40px] py-2 px-3 border' style={{ borderColor: 'var(--color-stroke-light, #E5E5E5)' }}>
          <span className='body text-gray-800'>
            Monthly allowance:{' '}
            {isLoadingAllowance
              ? (
                <span className='inline-flex items-center'>
                  <LoadingSpinnerCentered height='16px' width='16px' />
                </span>
                )
              : formatMoneyValue(monthlyAllowance)}
          </span>
        </div>
      </div>

      <div className='space-y-6'>
        <div>
          <CostBreakdownItem
            label={`Prepayment for ${nextMonthYearFormatted} insurance premiums`}
            value={formatMoneyValue(prefundingAmount)}
            description={`Billing in advance for insurance coverage from ${periodStartFormatted} – ${periodEndFormatted}.`}
          />
        </div>

        <div>
          <CostBreakdownItem
            label='Coverage Corrections'
            value={formatMoneyValue(coverageCorrections)}
            description={`Corrections for ${monthName} healthcare costs since the prior billing period.`}
          />
          <div className='mt-2'>
            <TextLink color='purple' href='#'>
              Why was I billed for 2 months of coverage?
            </TextLink>
          </div>
        </div>

        <div>
          <CostBreakdownItem
            label='Qualified Medical Expenses'
            value={formatMoneyValue(qmeTotal)}
            description='Money your team used from their leftover allowance to cover copays, prescriptions, etc.'
          />
          <div className='mt-2 space-y-1'>
            <CostBreakdownItem
              label='Ineligible spend'
              value={formatMoneyValue(ineligibleSpend)}
              indented
              variant='subdued'
            />
            <CostBreakdownItem
              label='Qualified medical expenses'
              value={formatMoneyValue(qualifiedExpenses)}
              indented
              variant='subdued'
            />
          </div>
        </div>

        <hr className='my-6 border-t border-stroke-[#919191]' />

        <div>
          <CostBreakdownItem
            label='Total healthcare costs'
            value={formatMoneyValue(totalAmount)}
            description={`The total amount billed for ${firstName}`}
            variant='bold'
          />
        </div>
      </div>
    </div>
  )
}
